import { Component } from 'react';

import BpkImage from '@skyscanner/backpack-web/bpk-component-image';

import AlternativeImage from '../AlternativeImage';
import { FadingLazyLoadedImage } from '../Image';

import type { Maybe } from '@shared/types/utils';

type Props = {
  width: number;
  height: number;
  className?: string;
  src?: Maybe<string>;
  forceHttps?: Maybe<boolean>;
  lazyLoad?: Maybe<boolean>;
  altText?: Maybe<string>;
};

type State = {
  error: boolean;
};

const defaultProps = {
  src: null,
  forceHttps: true,
  lazyLoad: false,
  altText: '',
};

class AdaptiveImage extends Component<Props, State> {
  static defaultProps = defaultProps;

  constructor(props: Props) {
    super(props);

    this.onError = this.onError.bind(this);

    this.state = {
      error: false,
    };
  }

  onError() {
    this.setState({ error: true });
  }

  getSrc() {
    const { forceHttps, height: h, src, width: w } = this.props;
    const { error } = this.state;

    if (!src || error) {
      return null;
    }

    let url = src;
    if (forceHttps) {
      url = url.replace('http://', 'https://');
    }
    return url.replace('_WxH', `_${w}x${h}`);
  }

  getSrcSet() {
    const { forceHttps, height: h, src, width: w } = this.props;

    if (src) {
      let url = src;
      if (forceHttps) {
        url = url.replace('http://', 'https://');
      }

      const scale = (x: number, factor: number) => Math.floor(x * factor);

      const urls = [
        `${url.replace('_WxH', `_${scale(w, 1)}x${scale(h, 1)}`)} 1x`,
        `${url.replace('_WxH', `_${scale(w, 1.5)}x${scale(h, 1.5)}`)} 1.5x`,
        `${url.replace('_WxH', `_${scale(w, 2)}x${scale(h, 2)}`)} 2x`,
        `${url.replace('_WxH', `_${scale(w, 3)}x${scale(h, 3)}`)} 3x`,
      ];
      return urls.join(', ');
    }
    return null;
  }

  render() {
    const {
      altText,
      forceHttps,
      height,
      lazyLoad,
      src: srcProps,
      width,
      ...rest
    } = this.props;
    const src = this.getSrc();

    const Image = lazyLoad ? FadingLazyLoadedImage : BpkImage;
    const alt = altText || '';

    return src ? (
      <Image
        src={src}
        srcSet={this.getSrcSet()}
        onError={this.onError}
        altText={alt}
        aspectRatio={width / height}
        {...rest}
      />
    ) : (
      <AlternativeImage {...rest} alt={alt} />
    );
  }
}

export default AdaptiveImage;
